import React, { type JSX, useEffect, useState } from "react";
import { Space, Tabs } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";

import Text from "../../../components/Text/Text";
import { numberFormatter } from "../../../utility/Formatter";
import HistoryCredit from "./HistoryCredit";
import { CardContainer } from "../../../components/Card/CardContainer";
import PageTitleNested from "../../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../../components/BreadCrumb/BreadCrumb";
import { getDetail } from "../../../datasource/OrderCreditDatasource";
import { creditLimitDetailState } from "../../../store/CreditLimitDetail";
import {
  ShopDetailEntity_INIT,
  type CreditLimitEntity,
  type HistoryCreditLimitEntity,
  type ShopDetailEntity,
} from "../../../entities/CreditLimitEntity";
import {
  countTempCreditLimit,
  getTempCreditLimitDetail,
  historyTempCreditLimit,
} from "../../../datasource/CreditLimitDatasource";
import CreditRequestLayout from "./CreditRequest";
import OrderDetailLayout from "./OrderDetailLayout";
import moment from "moment";
import { normalizeStatus, STATUS_CREDIT } from "../../../definitions/creditStatus";
import { getHistoryOrder, getOrderDetail } from "../../../datasource/OrderDatasourc";
import type { OrderEntity } from "../../../entities/OrderEntity";
import { getCustomerByCustomerNo } from "../../../datasource/CustomerDatasource";
import {
  SoApproversEntity_INIT,
  type SoApproversEntity,
} from "../../../entities/SoApproversEntity";
import {
  CreditApproversEntity_INIT,
  type CreditApproversEntity,
} from "../../../entities/CreditApproversEntity";
import EvidenceSection from "../SettingCredit/EvidenceSection";
import { getSignImg } from "../../../datasource/OverCreditApproveDatasource";
import { set } from "lodash";

function DetailCredit() {
  const [countHistory, setCountHistory] = useState(0);
  const [activeTabKey, setActiveTabKey] = useState<string>("DETAIL");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const creditTabs = localStorage.getItem("creditTabs");
  const { pathname } = window.location;
  const pathSplit = pathname.split("/");
  const id = pathSplit[3];
  const [recoilState, setRecoilState] = useRecoilState(creditLimitDetailState);
  const location = useLocation();
  const tempId = location.state?.tempId;
  const [status, setStatus] = useState<string>("");
  const [shopDetail, setShopDetail] = useState<ShopDetailEntity>(ShopDetailEntity_INIT);
  const [creditRequest, setCreditRequest] = useState<CreditLimitEntity | null>(null);
  const [orderProduct, setOrderProduct] = useState<OrderEntity | null>(null);
  const [orderShopDetail, setOrderShopDetail] = useState<ShopDetailEntity>(ShopDetailEntity_INIT);
  const [orderSoApprovers, setOrderSoApprovers] = useState<SoApproversEntity[]>([]);
  const [orderCreditApprovers, setOrderCreditApprovers] = useState<CreditApproversEntity[]>([]);
  const [dataHis, setDataHis] = useState<HistoryCreditLimitEntity[]>();

  useEffect(() => {
    if (!creditTabs) return;
    const idHis = creditTabs === "ORDER" ? id : tempId;
    if (!idHis) return;
    const loadCreditData = async () => {
      try {
        setLoading(true);

        const [creditDetail, editCount, detail, history] = await Promise.all([
          getTempCreditLimitDetail(tempId),
          countTempCreditLimit(tempId),
          getDetail(id),
          historyTempCreditLimit(idHis),
        ]);
        setCreditRequest(creditDetail);
        setStatus(creditDetail.status);
        setCountHistory(editCount.countHistory);
        setShopDetail(detail);
        if (detail.customerNo) {
          setRecoilState((prev) => ({ ...prev, customerNo: detail.customerNo }));
        }
        setDataHis(history);
      } catch (err) {
        console.error("Error loading credit data:", err);
      } finally {
        setLoading(false);
      }
    };

    const loadOrderData = async () => {
      try {
        setLoading(true);

        const [orderRes, history] = await Promise.all([getOrderDetail(id), getHistoryOrder(idHis)]);
        setOrderProduct(orderRes);
        setStatus(orderRes?.status || "");
        setDataHis(history);
        setCountHistory(history.length || 0);
        const detail = await getDetail(orderRes.customerCompanyId);
        setOrderShopDetail({ ...detail, orderNo: orderRes.orderNo });

        const overCredit = orderRes.orderOverCredits?.[0];
        const creditInfo = {
          requestNo: orderRes.soNo,
          userRequest: overCredit?.createBy,
          requestDate: orderRes.createAt,
          updatedAt: orderRes.updateAt,
          remarkRequest: overCredit?.reason,
          creditLimitRequest: overCredit?.requestedOverCredit,
          creditLimitRequestPercent:
            (Number(overCredit?.requestedOverCredit) * 100) /
            (Number(overCredit?.primaryCredit) + Number(overCredit?.temporaryCredit)),
        };
        setCreditRequest(creditInfo as any);

        if (orderRes?.customerNo) {
          const customerRes = await getCustomerByCustomerNo(orderRes.customerCompanyId);
          const customer = customerRes.customerCompany?.[0];
          if (customer) {
            setOrderShopDetail((prev) => ({
              ...prev,
              customerNo: customer.customerNo,
              customerName: customer.customerName,
              taxNo: customerRes.taxNo,
              ownerFirstname: customerRes.ownerFirstname,
              ownerLastname: customerRes.ownerLastname,
              telephone: customerRes.telephone,
              zone: customer.zone,
              arOverCredit: overCredit?.accumulatedOverCredit,
              creditLimitTotal: overCredit?.sumOverCredit,
              creditLimitTotalPercent: overCredit?.percentOverCredit,
              arOverCreditPercent:
                (Number(overCredit?.accumulatedOverCredit) * 100) /
                (Number(overCredit?.primaryCredit) + Number(overCredit?.temporaryCredit)),
            }));
          }
        }
        const getOrderApprover = (soApprovers: SoApproversEntity[]) => {
          const approved = soApprovers.find((a) => a.action !== "PENDING");
          if (approved) return [approved];

          const firstSequenceRequired = soApprovers.find((a) => a.sequenceRequired === 1);
          return firstSequenceRequired ? [firstSequenceRequired] : [];
        };

        const approvers = {
          credit: orderRes.orderCreditApprovers || [],
          order: getOrderApprover(orderRes.orderSoApprovers || []),
        };
        if (approvers.credit.length || approvers.order.length) {
          const [imgsCredit, imgsOrder] = await Promise.all([
            getSignImg({ userStaffIds: approvers.credit.map((a) => a.userStaffId) }),
            getSignImg({ userStaffIds: approvers.order.map((a) => a.userStaffId) }),
          ]);

          const mapApprovers = (arr: any[], imgs: any[]) =>
            arr.map((a) => ({
              ...a,
              signImageUrl:
                imgs.find((img) => img.userStaffId === a.userStaffId)?.signImageUrl || null,
            }));

          setOrderCreditApprovers(mapApprovers(approvers.credit, imgsCredit));
          setOrderSoApprovers(mapApprovers(approvers.order, imgsOrder));
        }
      } catch (err) {
        console.error("Error loading order data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (creditTabs === "CREDIT") loadCreditData();
    if (creditTabs === "ORDER") loadOrderData();
  }, [creditTabs, id, tempId]);

  const normalizedStatus = normalizeStatus(status);
  const meta = STATUS_CREDIT[normalizedStatus] || { label: "-", color: "gray" };
  const tabsData: { key: string; label: React.ReactNode; children?: JSX.Element | undefined }[] = [
    {
      label: (
        <Text fontSize={14} fontWeight={700}>
          รายละเอียด
        </Text>
      ),
      key: "DETAIL",
      children: (
        <>
          <CreditRequestLayout
          creditImageFiles={orderProduct?.overCreditFiles || []}
            data={creditTabs === "ORDER" ? orderShopDetail : shopDetail}
            creditRequest={creditRequest}
            creditTabs={creditTabs}
          />
          {creditTabs === "ORDER" && (
            <>
              <OrderDetailLayout orderProduct={orderProduct} />
              {orderCreditApprovers.length > 0 && (
                <EvidenceSection
                  data={orderCreditApprovers}
                  title='หลักฐานการอนุมัติวงเงินเกิน'
                  type='credit'
                  orderData={orderProduct || undefined}
                  otherApprovers={orderSoApprovers}
                />
              )}
              {orderSoApprovers.length > 0 && (
                <EvidenceSection
                  data={orderSoApprovers}
                  title='หลักฐานการอนุมัติใบสั่งขาย'
                  type='so'
                  orderData={orderProduct || undefined}
                  otherApprovers={orderCreditApprovers}
                />
              )}
            </>
          )}
        </>
      ),
    },
    {
      label: (
        <Text fontSize={14} fontWeight={700}>
          ประวัติ ({numberFormatter(countHistory, 0)})
        </Text>
      ),
      key: "HISTORY",
      children: <HistoryCredit dataHis={dataHis || []} callBackCount={(e) => setCountHistory(e)} />,
    },
  ];

  return (
    <CardContainer>
      <PageTitleNested
        title={`รายละเอียดคำขอวงเงิน`}
        showBack
        onBack={() => navigate(-1)}
        extra={
          <Space direction='vertical' size={1} style={{ display: "flex", alignItems: "end" }}>
            <Text
              fontSize={20}
              fontWeight={700}
              style={{
                color: meta.color,
                fontWeight: 700,
                marginRight: 12,
              }}
            >
              {meta.label}
            </Text>
            <Text fontFamily='Sarabun' fontSize={14}>
              {moment(creditRequest?.updatedAt).format("DD/MM/YYYY, HH:mm")}
            </Text>
          </Space>
        }
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "เช็คคำขอวงเงิน", path: -1 },
              { text: `รายละเอียดคำขอวงเงิน`, path: "" },
            ]}
          />
        }
      />
      <Tabs
        items={tabsData}
        activeKey={activeTabKey}
        onChange={(e) => {
          setActiveTabKey(e);
        }}
        style={{ marginTop: "2%", padding: "10px" }}
      />
    </CardContainer>
  );
}

export default DetailCredit;
